'use client';

import { useState, useMemo, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { ArrowRight, MapPin, Truck } from 'lucide-react';

import { useRouter } from '@/i18n/routing';
import { useHashParamDialog } from '@/hooks/use-hash-dialog';
import { useAccessibleRoute } from '@/hooks/use-route-access';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import type { Shipment, ShipmentPriority } from '../types';
import {
  useAssignBoxesMutation,
  useCreateShipmentMutation,
  useSaveDraftMutation
} from '../services/shipping.mutations';
import type { Driver } from '@/features/drivers';
import { SHIPPING_HASH } from '../constants';
import { DriverSelectionPanel } from './driver-selection-panel';
import { BoxSelectionPanel } from './box-selection-panel';
import { ShippingAssignFooter } from './shipping-assign-footer';
import { ShipmentQrDialog } from './shipment-qr-dialog';

export function ShippingAssignPage() {
  const t = useTranslations('shipping');
  const router = useRouter();
  const backTo = useAccessibleRoute();

  // ── State ────────────────────────────────────────────────────────────────
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [selectedBoxIds, setSelectedBoxIds] = useState<number[]>([]);
  const [deliveryLocation, setDeliveryLocation] = useState('');
  const [priority, setPriority] = useState<ShipmentPriority>('normal');
  // QR dialog open-state lives in the URL hash (#qr/<id>); the payload it
  // displays stays in local state and is set right before opening.
  const { open: qrOpen, openWith: openQr } = useHashParamDialog(
    SHIPPING_HASH.qr
  );
  const [qrData, setQrData] = useState<{
    shipmentNumber: string;
    qrCode: string;
    shipment?: Shipment;
  }>({ shipmentNumber: '', qrCode: '' });

  const selectedDriverId = selectedDriver?.id ?? null;

  // ── Mutations ────────────────────────────────────────────────────────────
  const createShipmentMutation = useCreateShipmentMutation();
  const assignBoxesMutation = useAssignBoxesMutation();
  const saveDraftMutation = useSaveDraftMutation();

  // ── Derived state ────────────────────────────────────────────────────────
  const totalWeight = useMemo(() => {
    return selectedBoxIds.length;
  }, [selectedBoxIds]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  function handleToggleBox(id: number) {
    setSelectedBoxIds((prev) =>
      prev.includes(id) ? prev.filter((bid) => bid !== id) : [...prev, id]
    );
  }

  function handleSelectAll(readyBoxIds: number[]) {
    setSelectedBoxIds(readyBoxIds);
  }

  async function handleConfirm() {
    if (!selectedDriverId || selectedBoxIds.length === 0) return;

    try {
      // Create shipment first, then assign boxes
      const shipment = await createShipmentMutation.mutateAsync({
        priority,
        box_count: selectedBoxIds.length,
        location: deliveryLocation || undefined
      });

      const shipmentData = (
        shipment as { data?: { id?: number; shipment_number?: string } }
      )?.data;
      const shipmentId = shipmentData?.id ?? 0;

      if (shipmentId > 0) {
        const driverProfileId = selectedDriver?.driver_profile?.id;
        if (!driverProfileId) return;

        // assignBoxes sets qr_code on the backend
        const assignResult = await assignBoxesMutation.mutateAsync({
          id: shipmentId,
          data: {
            driver_id: driverProfileId,
            box_ids: selectedBoxIds
          }
        });

        const assignedShipment = (assignResult as { data?: Shipment })?.data;

        // Show QR dialog if qr_code is available after assignment
        if (assignedShipment?.qr_code) {
          setQrData({
            shipmentNumber:
              assignedShipment.shipment_number ??
              shipmentData?.shipment_number ??
              '',
            qrCode: assignedShipment.qr_code,
            shipment: assignedShipment
          });
          openQr(String(assignedShipment.id));
          return;
        }
      }

      router.push(backTo('/dashboard/shipping'));
    } catch {
      // handled by global MutationCache
    }
  }

  const handleQrDialogClose = useCallback(
    (open: boolean) => {
      // Pushing the route replaces the hash, which closes the overlay.
      if (!open) router.push(backTo('/dashboard/shipping'));
    },
    [router, backTo]
  );

  async function handleSaveDraft() {
    try {
      await saveDraftMutation.mutateAsync({
        priority,
        box_count: selectedBoxIds.length,
        notes: selectedDriverId
          ? `driver_id:${selectedDriverId};box_ids:${selectedBoxIds.join(',')}`
          : `box_ids:${selectedBoxIds.join(',')}`
      });
      router.push(backTo('/dashboard/shipping'));
    } catch {
      // handled by global MutationCache
    }
  }

  return (
    <div className='flex flex-1 flex-col'>
      {/* Header */}
      <div className='border-b p-4'>
        <div className='flex items-center gap-2'>
          <Button variant='ghost' size='icon' onClick={() => router.back()}>
            <ArrowRight className='h-4 w-4' />
          </Button>
          <div>
            <div className='flex items-center gap-2'>
              <h1 className='text-xl font-bold'>{t('assignBoxesToDriver')}</h1>
              <Badge variant='secondary'>{t('newShipment')}</Badge>
            </div>
            <p className='text-muted-foreground text-sm'>
              {t('assignBoxesDescription')}
            </p>
          </div>
        </div>

        {/* Summary bar */}
        <Card className='bg-primary text-primary-foreground mt-4'>
          <CardContent className='flex flex-wrap items-center justify-between gap-4 py-3'>
            <div className='flex items-center gap-2'>
              <Truck className='h-5 w-5' />
              <span className='font-medium'>{t('shipmentSummary')}</span>
            </div>
            <div className='flex items-center gap-6 text-sm'>
              <div>
                <span className='opacity-75'>{t('totalBoxes')}: </span>
                <span className='font-bold'>{selectedBoxIds.length}</span>
              </div>
              <div>
                <span className='opacity-75'>{t('selectedDriver')}: </span>
                <span className='font-bold'>
                  {selectedDriver?.name ?? t('notSelected')}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main split layout */}
      <div className='grid flex-1 gap-6 p-6 lg:grid-cols-[1fr_2fr]'>
        <div className='flex flex-col gap-6'>
          <DriverSelectionPanel
            selectedDriverId={selectedDriverId}
            onSelectDriver={setSelectedDriver}
          />

          {/* Priority */}
          <Card>
            <CardContent className='space-y-2 p-4'>
              <Label>{t('priority')}</Label>
              <Select
                value={priority}
                onValueChange={(v) => setPriority(v as ShipmentPriority)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='normal'>{t('priorityNormal')}</SelectItem>
                  <SelectItem value='high'>{t('priorityHigh')}</SelectItem>
                  <SelectItem value='urgent'>{t('priorityUrgent')}</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Delivery Location */}
          <Card>
            <CardContent className='space-y-2 p-4'>
              <Label className='flex items-center gap-2'>
                <MapPin className='text-muted-foreground h-4 w-4' />
                {t('deliveryLocation')}
              </Label>
              <div className='relative'>
                <Input
                  value={deliveryLocation}
                  onChange={(e) => setDeliveryLocation(e.target.value)}
                  placeholder={t('deliveryLocationPlaceholder')}
                  className='pe-10'
                />
                <MapPin className='text-muted-foreground absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2' />
              </div>
            </CardContent>
          </Card>
        </div>

        <BoxSelectionPanel
          selectedBoxIds={selectedBoxIds}
          onToggleBox={handleToggleBox}
          onSelectAll={handleSelectAll}
          onClearSelection={() => setSelectedBoxIds([])}
        />
      </div>

      {/* QR Code Dialog */}
      <ShipmentQrDialog
        open={qrOpen}
        onOpenChange={handleQrDialogClose}
        shipmentNumber={qrData.shipmentNumber}
        qrCode={qrData.qrCode}
        shipment={qrData.shipment}
      />

      {/* Footer */}
      <ShippingAssignFooter
        selectedBoxCount={selectedBoxIds.length}
        totalWeight={totalWeight}
        selectedDriver={selectedDriver}
        onSaveDraft={handleSaveDraft}
        onConfirm={handleConfirm}
        isConfirming={
          createShipmentMutation.isPending || assignBoxesMutation.isPending
        }
        isDraftSaving={saveDraftMutation.isPending}
      />
    </div>
  );
}
